import React from 'react';
import { CostCenterStrategy } from './PurchaseOrderFormData';
import { Settings } from 'lucide-react';

interface CostCenterStrategySelectorProps {
  strategy: CostCenterStrategy;
  onChange: (strategy: CostCenterStrategy) => void;
}

export const CostCenterStrategySelector: React.FC<CostCenterStrategySelectorProps> = ({ strategy, onChange }) => {
  return (
    <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Settings className="w-5 h-5 text-primary" />
          Estrategia de Centros de Costo
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Define cómo se asignarán los costos de la orden
        </p>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* GLOBAL */}
          <label
            className={`flex flex-col p-4 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent ${
              strategy === 'GLOBAL' ? 'border-primary bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <div className="flex items-start">
              <input
                type="radio"
                name="strategy"
                value="GLOBAL"
                checked={strategy === 'GLOBAL'}
                onChange={(e) => onChange(e.target.value as CostCenterStrategy)}
                className="w-5 h-5 mt-0.5 mr-3"
              />
              <div className="flex-1">
                <div className="mb-1 text-base font-semibold text-foreground">Centro de Costo Global</div>
                <p className="text-sm text-muted-foreground">
                  Todos los items de la orden se asignan al mismo centro de costo y rubro.
                </p>
                <div className="px-3 py-1 mt-2 text-xs font-medium rounded-full w-fit bg-primary/10 text-primary">
                  Más simple
                </div>
              </div>
            </div>
          </label>

          {/* PER_LINE */}
          <label
            className={`flex flex-col p-4 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent ${
              strategy === 'PER_LINE' ? 'border-primary bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <div className="flex items-start">
              <input
                type="radio"
                name="strategy"
                value="PER_LINE"
                checked={strategy === 'PER_LINE'}
                onChange={(e) => onChange(e.target.value as CostCenterStrategy)}
                className="w-5 h-5 mt-0.5 mr-3"
              />
              <div className="flex-1">
                <div className="mb-1 text-base font-semibold text-foreground">Distribución por Línea</div>
                <p className="text-sm text-muted-foreground">
                  Cada item puede distribuirse entre diferentes centros de costo y rubros según la cantidad.
                </p>
                <div className="px-3 py-1 mt-2 text-xs font-medium rounded-full w-fit bg-primary/10 text-primary">
                  Más flexible
                </div>
              </div>
            </div>
          </label>

          {/* UNASSIGNED */}
          <label
            className={`flex flex-col p-4 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent ${
              strategy === 'UNASSIGNED' ? 'border-primary bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <div className="flex items-start">
              <input
                type="radio"
                name="strategy"
                value="UNASSIGNED"
                checked={strategy === 'UNASSIGNED'}
                onChange={(e) => onChange(e.target.value as CostCenterStrategy)}
                className="w-5 h-5 mt-0.5 mr-3"
              />
              <div className="flex-1">
                <div className="mb-1 text-base font-semibold text-foreground">Sin Asignar</div>
                <p className="text-sm text-muted-foreground">
                  Los gastos se asignarán al rubro general del sistema por defecto.
                </p>
                <div className="px-3 py-1 mt-2 text-xs font-medium rounded-full w-fit bg-muted text-muted-foreground">
                  Por defecto
                </div>
              </div>
            </div>
          </label>
        </div>

        {strategy === 'PER_LINE' && (
          <div className="p-3 border-l-4 border-primary rounded-r-lg bg-primary/5">
            <p className="text-sm text-foreground">
              <strong>Nota:</strong> Con esta opción podrás asignar cantidades específicas de cada item a diferentes obras o centros generales.
            </p>
          </div>
        )}

        {strategy === 'UNASSIGNED' && (
          <div className="p-3 border-l-4 border-border rounded-r-lg bg-muted/30">
            <p className="text-sm text-muted-foreground">
              <strong>Nota:</strong> Los gastos se asignarán automáticamente al rubro general del sistema configurado por defecto.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
